import clsx from 'clsx';
import { Form, Formik, useFormikContext } from 'formik';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import AutocompleteField from '@components/Form/AutocompleteField';
import IconPlus from '@components/Images/Rebranding/IconPlus';
import { useEventTracking } from '@hooks/index';
import { ToggleButton } from '@mui/material';
import { ClickTarget, click } from '@tracking';
import { autocompleteEntities } from '@utils/backend-api/entities';
import { ProfileParams } from '@utils/backend-api/userProfile';

import topicsSchema from './schema';

export interface Topic {
  key: string;
  label: string;
  default: boolean;
}

const INITIAL_FORM_STATE = { entity: { label: '', value: '' } };

interface StepTopicsProps {
  topics: Topic[];
  isProSubscriber: boolean;
  updateProfile: (profile: ProfileParams) => Promise<Record<string, never>>;
  onStepComplete: () => void;
  stepSaveRef: MutableRefObject<(() => void | Promise<void>) | null>;
}

interface EntityFormValues {
  entity: { label: string; value: string | number };
}

function SyncTopicFromAutocomplete({
  onEntitySelected,
}: {
  onEntitySelected: (key: string, label: string) => void;
}) {
  const { values, setFieldValue } = useFormikContext<EntityFormValues>();

  useEffect(() => {
    const entity = values.entity;
    if (!entity?.label || entity?.value == null || entity?.value === '') return;
    const key = String(entity.value);
    onEntitySelected(key, entity.label);
    setFieldValue('entity', { label: '', value: '' });
  }, [values.entity, onEntitySelected, setFieldValue]);

  return null;
}

const StepTopics = ({
  topics,
  isProSubscriber,
  updateProfile,
  onStepComplete,
  stepSaveRef,
}: StepTopicsProps) => {
  const [initialValues] = useState(INITIAL_FORM_STATE);
  const [selectedTopicKeys, setSelectedTopicKeys] = useState<string[]>([]);
  const [addedTopics, setAddedTopics] = useState<Topic[]>([]);
  const { trackEvent } = useEventTracking();
  useEffect(() => {
    stepSaveRef.current = async () => {
      await updateProfile({ topics: selectedTopicKeys });
      onStepComplete();
    };
    return () => {
      stepSaveRef.current = null;
    };
  }, [stepSaveRef, updateProfile, onStepComplete, selectedTopicKeys]);

  const displayTopics = useMemo(() => {
    const addedKeys = new Set(addedTopics.map((t) => t.key));
    const existingTopics = topics.filter((t) => !addedKeys.has(t.key));
    return [...addedTopics, ...existingTopics];
  }, [topics, addedTopics]);

  const handleEntitySelected = useCallback(
    (key: string, label: string) => {
      setSelectedTopicKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
      setAddedTopics((prev) => {
        const alreadyInTopics = topics.some((t) => t.key === key);
        const alreadyAdded = prev.some((t) => t.key === key);
        if (alreadyInTopics || alreadyAdded) return prev;
        return [...prev, { key, label, default: false }];
      });
    },
    [topics]
  );

  const handleButtonToggle = (topic: Topic) => {
    trackEvent(click({ clickTarget: ClickTarget.TopicAdd, clickText: topic.label }));
    setSelectedTopicKeys((prev) =>
      prev.includes(topic.key) ? prev.filter((k) => k !== topic.key) : [...prev, topic.key]
    );
  };

  const isSelected = (topic: Topic) => selectedTopicKeys.includes(topic.key);

  const handleRemoveTopic = (e: React.MouseEvent, topic: Topic) => {
    e.stopPropagation();
    trackEvent(click({ clickTarget: ClickTarget.TopicRemove, clickText: topic.label }));
    setSelectedTopicKeys((prev) => prev.filter((k) => k !== topic.key));
  };

  return (
    <div id="onboarding-topics" className="flex flex-col">
      <p className="hidden font-suisse-intl text-description-lg md:block">
        Search topics to follow
      </p>
      <div className="w-[340px]">
        <Formik initialValues={initialValues} onSubmit={() => {}} validationSchema={topicsSchema}>
          <div className="hidden md:block">
            <SyncTopicFromAutocomplete onEntitySelected={handleEntitySelected} />
            <Form className="form-container pb-24">
              <AutocompleteField
                name="entity"
                className="mb-16 mt-0"
                isCreatable={false}
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                  })
                }
                placeholder="Select"
              />
            </Form>
          </div>
        </Formik>
        <div className="block md:hidden">
          <h4 className="m-0 font-suisse-works text-headline-xxs">Your News Feed</h4>
          <p className="mb-24 mt-0 font-suisse-screen text-byline-sm">
            Select the topics you’re most interested in.
          </p>
        </div>
        <div className="flex flex-wrap gap-8">
          {displayTopics.map((topic) => (
            <ToggleButton
              className={clsx('titoggle', {
                'for-pro': isProSubscriber,
              })}
              key={topic.key}
              value={topic.key}
              selected={isSelected(topic)}
              disableRipple
              onChange={() => handleButtonToggle(topic)}
            >
              <span className="flex gap-8">
                {topic.label}
                {isSelected(topic) && (
                  <IconPlus
                    onClick={(e) => handleRemoveTopic(e, topic)}
                    className="w-[12px] rotate-45 stroke-[2] text-white"
                  />
                )}
              </span>
            </ToggleButton>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StepTopics;
